import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Button, Field, Text, VStack } from '@chakra-ui/react';
import { EmptyState } from '@src/components/chakra-ui/empty-state';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { SessionAvailabilityPicker } from '@src/components/sco/Advisors/shared/SessionAvailabilityPicker';
import { SessionWeeklyHoursEditor } from '@src/components/sco/Advisors/shared/SessionWeeklyHoursEditor';
import { TimezoneSelect } from '@src/components/sco/Advisors/shared/TimezoneSelect';
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat';
import { dayRowsToWeeklyHours, parseSessionWeeklyHours, weeklyHoursToDayRows, type SessionDayHours } from '@src/components/sco/Advisors/utils/sessionWeeklyHours';
import { defaultSessionTimezone } from '@src/components/sco/Advisors/utils/timezoneSelect';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { UPSERT_ADVISOR_PROFILE } from '@src/gql/mutations/UpsertAdvisorProfile';
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useState } from 'react';
import { LuSave, LuUserRound } from 'react-icons/lu';
type ProfileQueryData = {
  myAdvisorProfile?: ScoAdvisorProfile | null;
};
export type SessionAvailabilityViewProps = {
  open: boolean;
  canAccept: boolean;
  timezone: string;
  days: SessionDayHours[];
  error: string | null;
  saved: boolean;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onTimezoneChange: (timezone: string) => void;
  onDaysChange: (days: SessionDayHours[]) => void;
  onSave: () => void;
};
export const SessionAvailabilityView = t0 => {
  const $ = _c(30);
  const {
    open,
    canAccept,
    timezone,
    days,
    error,
    saved,
    saving,
    onOpenChange,
    onTimezoneChange,
    onDaysChange,
    onSave
  } = t0;
  let t1;
  if ($[0] !== canAccept || $[1] !== onOpenChange || $[2] !== open) {
    t1 = <SessionAvailabilityPicker canAccept={canAccept} open={open} onOpenChange={onOpenChange} />;
    $[0] = canAccept;
    $[1] = onOpenChange;
    $[2] = open;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Field.Label>Timezone</Field.Label>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onTimezoneChange || $[6] !== timezone) {
    t3 = <TimezoneSelect value={timezone} onChange={onTimezoneChange} />;
    $[5] = onTimezoneChange;
    $[6] = timezone;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Field.HelperText>Hours below are in this timezone. Founders still propose times in their own.</Field.HelperText>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3) {
    t5 = <Field.Root>{t2}{t3}{t4}</Field.Root>;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <VStack align="stretch" gap={1}><Text fontWeight="medium" textStyle="sm">Weekly hours</Text><Text color="colorPalette.fg/60" fontFamily="longform" textStyle="sm">Days and ranges you prefer to meet. Founders still suggest times; you confirm one.</Text></VStack>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== days || $[13] !== onDaysChange) {
    t7 = <VStack align="stretch" gap={3}>{t6}<SessionWeeklyHoursEditor days={days} onChange={onDaysChange} /></VStack>;
    $[12] = days;
    $[13] = onDaysChange;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== error) {
    t8 = error ? <Text color="red.fg" fontFamily="longform" fontSize="sm">{error}</Text> : null;
    $[15] = error;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== saved) {
    t9 = saved ? <Text color="green.fg" fontFamily="longform" fontSize="sm">Availability saved.</Text> : null;
    $[17] = saved;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LuSave />;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== onSave || $[21] !== saving) {
    t11 = <Button alignSelf="flex-start" loading={saving} variant="cta" onClick={onSave}>{t10} Save availability</Button>;
    $[20] = onSave;
    $[21] = saving;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t1 || $[24] !== t11 || $[25] !== t5 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t12 = <VStack align="stretch" gap={6}>{t1}{t5}{t7}{t8}{t9}{t11}</VStack>;
    $[23] = t1;
    $[24] = t11;
    $[25] = t5;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
const SessionAvailabilityForm = ({
  profile
}: {
  profile: ScoAdvisorProfile;
}) => {
  const [open, setOpen] = useState(!!profile.isSessionEnabled);
  const [timezone, setTimezone] = useState(defaultSessionTimezone(profile.timezone));
  const [days, setDays] = useState(() => weeklyHoursToDayRows(parseSessionWeeklyHours(profile.sessionWeeklyHours)));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [upsert, upsertState] = useMutation(UPSERT_ADVISOR_PROFILE, {
    refetchQueries: [{
      query: MY_ADVISOR_PROFILE
    }]
  });
  const handleSave = async () => {
    setError(null);
    setSaved(false);
    const hours = dayRowsToWeeklyHours(days);
    if (hours.some(hour => hour.end <= hour.start)) {
      setError('Each open day needs an end time after the start time.');
      return;
    }
    try {
      await upsert({
        variables: {
          input: {
            isSessionEnabled: open,
            timezone: timezone.trim() || null,
            sessionWeeklyHours: hours
          }
        }
      });
      setSaved(true);
    } catch (err) {
      setError(cleanErrorMessage(err, 'Could not save availability.'));
    }
  };
  return <SessionAvailabilityView canAccept={!!profile.canAcceptSessions} days={days} error={error} open={open} saved={saved} saving={upsertState.loading} timezone={timezone} onSave={() => void handleSave()} onDaysChange={next => {
    setDays(next);
    setSaved(false);
  }} onOpenChange={next_0 => {
    setOpen(next_0);
    setSaved(false);
  }} onTimezoneChange={next_1 => {
    setTimezone(next_1);
    setSaved(false);
  }} />;
};
export const SessionAvailabilitySection = () => {
  const $ = _c(8);
  const paths = useAdvisorsPaths();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fetchPolicy: "network-only"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const profileQuery = useQuery(MY_ADVISOR_PROFILE, t0);
  const profile = profileQuery.data?.myAdvisorProfile;
  const t1 = profileQuery.loading && !profile;
  let t2;
  if ($[1] !== paths || $[2] !== profile) {
    t2 = profile ? <SessionAvailabilityForm profile={profile} /> : <EmptyState description="Finish the application, then return here to set booking hours." icon={<LuUserRound />} size="sm" title="Create an advisor profile first"><Button asChild={true} variant="cta"><NextLink href={paths.apply}>Apply to advise</NextLink></Button></EmptyState>;
    $[1] = paths;
    $[2] = profile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== profileQuery.error || $[5] !== t1 || $[6] !== t2) {
    t3 = <AdvisorsQueryState error={profileQuery.error} loading={t1}>{t2}</AdvisorsQueryState>;
    $[4] = profileQuery.error;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};